/**
 * stringUtils.js
 * Funções utilitárias para manipulação e formatação de strings
 */

/**
 * Remove acentos e caracteres especiais de uma string
 * @param {string} text - Texto a ser normalizado
 * @returns {string} - Texto sem acentos
 */
export function removeAccents(text) {
  if (!text || typeof text !== 'string') return '';

  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, ''); // Remove acentos
}

/**
 * Normaliza uma string removendo acentos e convertendo para maiúsculo/minúsculo
 * @param {string} text - Texto a ser normalizado
 * @param {Object} options - Opções de normalização
 * @param {boolean} options.lowercase - Converte para minúsculas
 * @param {boolean} options.uppercase - Converte para maiúsculas
 * @param {boolean} options.removeAccents - Remove acentos
 * @returns {string} - Texto normalizado
 */
export function normalizeString(text, options = {}) {
  if (!text || typeof text !== 'string') return '';

  let normalized = text;

  if (options.removeAccents) {
    normalized = removeAccents(normalized);
  }

  if (options.uppercase) {
    normalized = normalized.toUpperCase();
  } else if (options.lowercase) {
    normalized = normalized.toLowerCase();
  }

  return normalized;
}

/**
 * Formata uma string para ter a primeira letra de cada palavra em maiúsculo
 * @param {string} text - Texto a ser formatado
 * @returns {string} - Texto formatado
 */
export function capitalizeWords(text) {
  if (!text || typeof text !== 'string') return '';

  return text
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Formata um código (CAR, SIGEF, etc.) removendo prefixos e formatando de acordo com o tipo
 * @param {string} code - Código a ser formatado
 * @param {string} type - Tipo de código (CAR, SIGEF, NIRF)
 * @returns {string} - Código formatado
 */
export function formatPropertyCode(code, type) {
  if (!code || typeof code !== 'string') return '';

  // Remove caracteres especiais conforme o tipo
  switch (type.toUpperCase()) {
    case 'CAR':
      return code.replace(/^#/, '').trim();
    case 'SIGEF':
      return code.replace(/^#/, '').trim();
    case 'NIRF':
    case 'CAFIR':
      return code.replace(/^#/, '').trim();
    default:
      return code.replace(/^#/, '').trim();
  }
}

// formatCode foi movida e substituída pela implementação mais abaixo

/**
 * Remove símbolos # de códigos
 * @param {string} code - Código a ser limpo
 * @returns {string} - Código sem o símbolo #
 */
export function removeHash(code) {
  if (!code || typeof code !== 'string') return '';
  return code.replace(/^#/, '').trim();
}

/**
 * Formata um CEP brasileiro para o padrão xxxxx-xxx
 * @param {string|number} value - CEP a ser formatado
 * @param {string} [defaultValue='-'] - Valor padrão quando o CEP é nulo/vazio
 * @returns {string} - CEP formatado ou valor original quando não for possível formatar
 */
export function formatCep(value, defaultValue = '-') {
  if (value === null || value === undefined || value === '') return defaultValue;

  const str = String(value).trim();

  // Se já estiver no padrão xxxxx-xxx, apenas retorna
  if (/^\d{5}-\d{3}$/.test(str)) {
    return str;
  }

  const digits = str.replace(/\D/g, '');

  if (digits.length !== 8) {
    // Não consegue formatar, retorna valor original para não esconder informação
    return str;
  }

  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

/**
 * Remove duplicatas de um array de strings
 * @param {string[]} array - Array com possíveis duplicatas
 * @returns {string[]} - Array sem duplicatas
 */
export function removeDuplicates(array) {
  if (!Array.isArray(array)) return [];
  return [...new Set(array)];
}

/**
 * Extrai domínio (CAR, SIGEF, etc) de uma chave ou nome de campo
 * @param {string} key - Nome do campo ou chave
 * @returns {string} - Domínio extraído ou string vazia
 */
export function extractDomainFromKey(key) {
  if (!key || typeof key !== 'string') return '';

  const domains = ['CAR', 'SIGEF', 'SNCR', 'CAFIR', 'NIRF'];

  // Verificar se a chave contém um dos domínios
  const upperKey = key.toUpperCase();

  for (const domain of domains) {
    if (upperKey.includes(domain)) {
      return domain;
    }
  }

  return '';
}

/**
 * Humaniza chaves de propriedades para exibição, capitalizando palavras e substituindo underscores por espaços
 * @param {string} key - Chave a ser humanizada
 * @returns {string} - Chave humanizada para exibição
 */
export function humanizeKey(key) {
  if (!key || typeof key !== 'string') return '';

  // Substitui underscores por espaços
  let humanized = key.replace(/_/g, ' ');

  // Substituições específicas para termos comuns em propriedades rurais
  humanized = humanized
    .replace(/\bimo\b/i, 'Imóvel')
    .replace(/\bco\b/i, 'Código')
    .replace(/\bgeojson\b/i, 'GeoJSON')
    .replace(/\bcpf\b/i, 'CPF')
    .replace(/\bcnpj\b/i, 'CNPJ')
    .replace(/\bmatrícula\b/i, 'Matrícula')
    .replace(/\bmatricula\b/i, 'Matrícula')
    .replace(/\bparcela\b/i, 'Parcela')
    .replace(/\bdata\b/i, 'Data')
    .replace(/\baprovacao\b/i, 'Aprovação')
    .replace(/\baprovação\b/i, 'Aprovação');

  // Capitaliza cada palavra
  return humanized
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Formata códigos para exibição baseado no tipo ou domínio
 * @param {string} code - Código a ser formatado
 * @param {string} type - Tipo ou domínio (CAR, SIGEF, etc)
 * @returns {string} - Código formatado para exibição
 */
export function formatCode(code, type = '') {
  if (!code || typeof code !== 'string') return '';

  // Remover # e espaços desnecessários
  const cleanCode = code.replace(/^#/, '').trim();

  // Formatar baseado no tipo/domínio
  switch (type.toUpperCase()) {
    case 'CAR':
      // Formatar CAR: XX-XXXX.XXXX.XXXX.XXXX.XXXX.XXXX.XXXX
      return cleanCode;

    case 'SIGEF':
      // Formatar SIGEF: limpar e retornar
      return cleanCode;

    case 'NIRF':
    case 'CAFIR':
      // Formatar NIRF: adicionar pontos se necessário
      return cleanCode;

    default:
      return cleanCode;
  }
}
